import {type DatesRangeValue} from '@mantine/dates'
import {useDebouncedValue} from '@mantine/hooks'
import {useNavigate, useSearch} from '@tanstack/react-router'
import {useEffect, useMemo, useRef, useState} from 'react'

import {
    buildJobListSearch,
    DEFAULT_JOB_STATUS_FILTER,
    jobListSearchEquals,
    type JobSortState,
    parseStatusSearchParam,
    searchToDateRange,
    searchToSortState,
    validateJobListSearch,
} from './jobs-search'
import type {JobLifecycleStatus} from './types'

export function useJobsListFilters(syncUrlSearch: boolean) {
    const navigate = useNavigate()
    const rawSearch = useSearch({strict: false})
    const urlSearch = useMemo(
        () => validateJobListSearch(rawSearch as Record<string, unknown>),
        [rawSearch],
    )
    const lastWrittenSearchRef = useRef(urlSearch)
    const restoringFromUrlRef = useRef(false)

    const [nameFilter, setNameFilter] = useState(() => urlSearch.name ?? '')
    const [customerIdFilter, setCustomerIdFilter] = useState<string | null>(
        () => urlSearch.customer ?? null,
    )
    const [serviceTypeNameFilter, setServiceTypeNameFilter] = useState(
        () => urlSearch.service ?? '',
    )
    const [scheduleDateRange, setScheduleDateRange] = useState<DatesRangeValue>(() =>
        searchToDateRange(urlSearch),
    )
    const [statusFilter, setStatusFilter] = useState<JobLifecycleStatus[]>(() =>
        urlSearch.status ? parseStatusSearchParam(urlSearch.status) : DEFAULT_JOB_STATUS_FILTER,
    )
    const [sortState, setSortState] = useState<JobSortState>(() => searchToSortState(urlSearch))

    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [debouncedServiceTypeName] = useDebouncedValue(serviceTypeNameFilter, 300)

    const intendedSearch = useMemo(
        () =>
            buildJobListSearch({
                name: nameFilter,
                customerId: customerIdFilter,
                serviceTypeName: serviceTypeNameFilter,
                scheduleDateRange,
                statusFilter,
                sortState,
            }),
        [
            nameFilter,
            customerIdFilter,
            serviceTypeNameFilter,
            scheduleDateRange,
            statusFilter,
            sortState,
        ],
    )

    useEffect(() => {
        if (jobListSearchEquals(urlSearch, lastWrittenSearchRef.current)) {
            return
        }

        lastWrittenSearchRef.current = urlSearch
        restoringFromUrlRef.current = true
        setNameFilter(urlSearch.name ?? '')
        setCustomerIdFilter(urlSearch.customer ?? null)
        setServiceTypeNameFilter(urlSearch.service ?? '')
        setScheduleDateRange(searchToDateRange(urlSearch))
        setStatusFilter(parseStatusSearchParam(urlSearch.status))
        setSortState(searchToSortState(urlSearch))
    }, [urlSearch])

    useEffect(() => {
        if (!syncUrlSearch) {
            return
        }

        if (restoringFromUrlRef.current) {
            restoringFromUrlRef.current = false
            return
        }

        if (jobListSearchEquals(intendedSearch, lastWrittenSearchRef.current)) {
            return
        }

        if (jobListSearchEquals(intendedSearch, urlSearch)) {
            lastWrittenSearchRef.current = intendedSearch
            return
        }

        lastWrittenSearchRef.current = intendedSearch
        void navigate({
            to: '/jobs',
            search: intendedSearch,
            replace: true,
        })
    }, [syncUrlSearch, intendedSearch, urlSearch, navigate])

    return {
        nameFilter,
        setNameFilter,
        customerIdFilter,
        setCustomerIdFilter,
        serviceTypeNameFilter,
        setServiceTypeNameFilter,
        scheduleDateRange,
        setScheduleDateRange,
        statusFilter,
        setStatusFilter,
        sortState,
        setSortState,
        debouncedName,
        debouncedServiceTypeName,
        scheduleDateFromFilter: scheduleDateRange[0],
        scheduleDateToFilter: scheduleDateRange[1],
    }
}
